import {
  Alert,
  Box,
  Chip,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { CaretDown, Info, Warning } from '@phosphor-icons/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttributeStatus } from 'companySettings/enums';
import {
  useGetPartFinderFamily,
  useGetPartFinderFamilyParts,
  useGetPublishedPartFinderProductTypes,
  usePartFinderFamilyActions,
} from 'companySettings/hooks';
import { partFinderFamilyListPath } from 'companySettings/routes';
import { IPartFinderFamily } from 'companySettings/types';
import { Breadcrumbs, Button, Confirmation, PageTitle, SlidingPanel } from 'core/components';
import { Loader } from 'core/elements';
import { NotificationPosition } from 'core/enums';
import { useLeavePageWarning } from 'core/hooks';
import { FormProvider, useFormContext } from 'core/states';
import { IBreadcrumbsItem } from 'core/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { AttributesStep } from '../UpsertPartFinderFamily/AttributesStep';
import { DetailsStep } from '../UpsertPartFinderFamily/DetailsStep';
import {
  DetachedAttributes,
  isInheritedAttribute,
  mapAttributesForProductType,
  mapPartFinderFamilyFormDefaults,
  mapPartFinderFamilyPayload,
} from '../UpsertPartFinderFamily/mappers';
import { PartsStep } from '../UpsertPartFinderFamily/PartsStep';
import { upsertPartFinderFamilySchema } from '../UpsertPartFinderFamily/schema';
import { IFamilyPartForm, IUpsertPartFinderFamilyFormData } from '../UpsertPartFinderFamily/types';
import {
  getFamilyUnsavedChanges,
  hasFamilyChanges,
  mapPartFromServer,
  movePartValueKeys,
  prunePartValues,
} from '../UpsertPartFinderFamily/utils';
import { getFamilyChangeLines } from '../utils';
import { PartFinderFamilyVersionHistory } from '../VersionHistory';

interface IContentProps {
  family: IPartFinderFamily;
  loadedParts: IFamilyPartForm[];
  // While the family is refetched after a save, its revision is the old one: a second save would
  // be refused as changed by someone else.
  isRefreshing: boolean;
}

// The family editor: its parts grid with the values, with Save ▾ and Manage Attributes around Add
// Part Numbers and Part Family Details on the right of the grid's toolbar. Everything is form state
// until one of the saves.
const PartFinderFamilyEditorContent = ({ family, loadedParts, isRefreshing }: IContentProps) => {
  const { t } = useTranslation();

  const [isAttributesOpen, setIsAttributesOpen] = useState(false);
  // The Manage Attributes drawer's sticky header, which takes the buttons above the cards.
  const [attributesToolbar, setAttributesToolbar] = useState<HTMLDivElement | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [holdOn, setHoldOn] = useState<{ count: number } | null>(null);
  const [detached, setDetached] = useState(DetachedAttributes.CONVERT);
  // What a drawer opened on, so Cancel / Discard changes can put it back.
  const openedOnRef = useRef<Partial<IUpsertPartFinderFamilyFormData>>({});

  const { handleSubmit, watch, getValues, setValue, trigger } = useFormContext();
  const { savePartFinderFamily } = usePartFinderFamilyActions(NotificationPosition.TOP);
  const { records: productTypes = [] } = useGetPublishedPartFinderProductTypes();

  const hasChanges = hasFamilyChanges(
    watch() as IUpsertPartFinderFamilyFormData,
    family,
    loadedParts
  );
  // A saved draft can be published as it is; a published family needs a change first.
  const canPublish = hasChanges || AttributeStatus.PUBLISHED !== family.status;
  const hasParts = (watch('parts') ?? []).length > 0;

  const { isLeaveConfirmOpen, leave, stay } = useLeavePageWarning(hasChanges);

  // What a save would record, worded as the version history words it: listed by the leave warning
  // and the Unsaved changes chip, and marked on the buttons that lead to it.
  const unsavedChanges = hasChanges
    ? getFamilyUnsavedChanges(
        watch() as IUpsertPartFinderFamilyFormData,
        family,
        loadedParts,
        productTypes
      )
    : {};
  const unsavedChangeLines = getFamilyChangeLines(unsavedChanges, t);
  const unsavedBadge = [{ key: 'unsavedChanges', color: Color.DANGER }];
  const hasUnsavedDetails = Boolean(
    unsavedChanges.name || unsavedChanges.productType || unsavedChanges.category
  );

  const onSave = (status: AttributeStatus) =>
    handleSubmit(async (data: IUpsertPartFinderFamilyFormData) => {
      await savePartFinderFamily(mapPartFinderFamilyPayload(data, family, status));
    })();

  const openAttributes = () => {
    openedOnRef.current = { attributes: getValues('attributes') };
    setIsAttributesOpen(true);
  };

  const openDetails = () => {
    openedOnRef.current = {
      name: getValues('name'),
      productTypeId: getValues('productTypeId'),
      category: getValues('category'),
    };
    setIsDetailsOpen(true);
  };

  const restore = () =>
    Object.entries(openedOnRef.current).forEach(([field, value]) => setValue(field, value));

  // A product type change detaches the inherited attributes: they become the family's own, values
  // kept, or go (mapAttributesForProductType()).
  const applyProductType = useCallback(
    (mode: DetachedAttributes) => {
      const productTypeId = getValues('productTypeId');
      const productType = productTypes.find(({ id }) => id === productTypeId);
      const previousProductTypeName =
        productTypes.find(({ id }) => id === getValues('attributesProductTypeId'))?.name ??
        family.productTypeName ??
        '';
      const { attributes, movedKeys } = mapAttributesForProductType(
        getValues('attributes') ?? [],
        productType,
        mode,
        previousProductTypeName
      );

      setValue('attributes', attributes);
      setValue(
        'parts',
        prunePartValues(movePartValueKeys(getValues('parts') ?? [], movedKeys), attributes)
      );
      setValue('attributesProductTypeId', productType ? productTypeId : null);
    },
    [family.productTypeName, getValues, productTypes, setValue]
  );

  // The details are checked before the drawer closes: the page has no form to show the error later.
  const onSaveDetails = async () => {
    if (!(await trigger(['name', 'productTypeId', 'category'], { shouldFocus: true }))) {
      return;
    }

    const inheritedCount = (getValues('attributes') ?? []).filter(isInheritedAttribute).length;

    if (getValues('productTypeId') !== getValues('attributesProductTypeId') && inheritedCount > 0) {
      setDetached(DetachedAttributes.CONVERT);
      setHoldOn({ count: inheritedCount });

      return;
    }

    if (getValues('productTypeId') !== getValues('attributesProductTypeId')) {
      applyProductType(DetachedAttributes.DELETE);
    }

    setIsDetailsOpen(false);
  };

  // The way back to the families list, as the app's other detail pages have it.
  const breadcrumbs: IBreadcrumbsItem[] = [
    { text: t('titlePartFinderFamilies'), to: partFinderFamilyListPath() },
    { text: watch('name') || family.name },
  ];

  // The editor fills the page, and the parts grid the space under its header.
  return (
    <Stack sx={{ gap: 2, flex: 1, minHeight: 0 }}>
      {/* The breadcrumb sits close to the title it leads to; the status pill is one of the
          title's chips, centred on the title line. */}
      <Stack sx={{ gap: 0.5 }}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <PageTitle
          title={watch('name') || family.name}
          chips={[
            // Its own box: the pill aligns itself to the top of its parent (it sits in a column
            // in the product type editor), and here the title row centres the box. The title's
            // letters sit below the middle of its line box, so the box goes 2px down (a 4px top
            // margin) to meet them.
            <Box key="versionHistory" sx={{ display: 'flex', mt: 0.5 }}>
              <PartFinderFamilyVersionHistory family={family} />
            </Box>,
            ...(hasChanges
              ? [
                  <Box key="unsavedChanges" sx={{ display: 'flex', mt: 0.5 }}>
                    <Tooltip
                      title={
                        unsavedChangeLines.length > 0 && (
                          <Stack component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                            {unsavedChangeLines.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </Stack>
                        )
                      }
                    >
                      <Chip color={Color.WARNING} variant={Variant.SOFT}>
                        {t('labelUnsavedChanges')}
                      </Chip>
                    </Tooltip>
                  </Box>,
                ]
              : []),
          ]}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <PartsStep
          // The parts grid is on the page here, not in the wizard's panel.
          drawerLevel={0}
          loadedParts={loadedParts}
          leadingButtons={[
            <Dropdown key="saveFamilyButton">
              <MenuButton
                color={Color.SUCCESS}
                variant={Variant.SOLID}
                endDecorator={<CaretDown />}
                disabled={isRefreshing || (!hasChanges && !canPublish)}
              >
                {t('buttonLabelSave')}
              </MenuButton>
              <Menu placement="bottom-start">
                <MenuItem
                  disabled={!canPublish || !hasParts}
                  onClick={() => onSave(AttributeStatus.PUBLISHED)}
                >
                  {t('buttonLabelSaveAndPublish')}
                </MenuItem>
                <MenuItem disabled={!hasChanges} onClick={() => onSave(AttributeStatus.DRAFT)}>
                  {t('buttonLabelSaveToDraft')}
                </MenuItem>
              </Menu>
            </Dropdown>,
          ]}
          trailingButtons={[
            <Button
              key="manageAttributesButton"
              text={t('buttonLabelManageAttributes')}
              variant={Variant.SOFT}
              onClick={openAttributes}
              badges={unsavedChanges.attributes ? unsavedBadge : undefined}
            />,
          ]}
          rightButtons={[
            <Button
              key="partFamilyDetailsButton"
              text={t('buttonLabelPartFamilyDetails')}
              variant={Variant.OUTLINED}
              startDecorator={<Info />}
              onClick={openDetails}
              badges={hasUnsavedDetails ? unsavedBadge : undefined}
            />,
          ]}
        />
      </Box>

      <SlidingPanel
        isOpen={isAttributesOpen}
        onClosePanel={() => {
          restore();
          setIsAttributesOpen(false);
        }}
        title={t('titleManageAttributes')}
        extra={<Box ref={setAttributesToolbar} />}
        size={Size.LG}
        buttons={
          <Stack direction={Direction.ROW} sx={{ gap: 1 }}>
            <Button
              text={t('buttonLabelUpdateAttributes')}
              onClick={() => setIsAttributesOpen(false)}
            />
            <Button
              text={t('buttonLabelCancel')}
              variant={Variant.OUTLINED}
              color={Color.NEUTRAL}
              onClick={() => {
                restore();
                setIsAttributesOpen(false);
              }}
            />
          </Stack>
        }
      >
        {isAttributesOpen && (
          <AttributesStep
            savedAttributes={family.attributes}
            toolbarContainer={attributesToolbar}
          />
        )}
      </SlidingPanel>

      <SlidingPanel
        isOpen={isDetailsOpen}
        onClosePanel={() => {
          restore();
          setIsDetailsOpen(false);
        }}
        title={t('titlePartFamilyDetails')}
        subtitle={t('labelLastUpdate', { date: family.updatedAt })}
        buttons={
          <Stack direction={Direction.ROW} sx={{ gap: 1 }}>
            <Button text={t('buttonLabelSaveChanges')} onClick={onSaveDetails} />
            <Button
              text={t('buttonLabelDiscardChanges')}
              variant={Variant.OUTLINED}
              color={Color.NEUTRAL}
              onClick={() => {
                restore();
                setIsDetailsOpen(false);
              }}
            />
          </Stack>
        }
      >
        {isDetailsOpen && (
          <Stack sx={{ gap: 2 }}>
            <Alert variant={Variant.SOFT} color={Color.WARNING} startDecorator={<Warning />}>
              {t('messagePartFinderFamilyProductTypeChange')}
            </Alert>
            <DetailsStep />
          </Stack>
        )}
      </SlidingPanel>

      <Confirmation
        isOpen={Boolean(holdOn)}
        color={Color.WARNING}
        title={t('titleHoldOnAMoment')}
        message={
          <Stack sx={{ gap: 2 }}>
            <Typography level={Level.BODY_MD}>
              {t('messagePartFinderFamilyDetachAttributes', { count: holdOn?.count ?? 0 })}
            </Typography>
            <RadioGroup
              value={detached}
              onChange={(event) => setDetached(event.target.value as DetachedAttributes)}
            >
              <Radio
                value={DetachedAttributes.CONVERT}
                label={t('labelConvertToFamilySpecific')}
                slotProps={{ label: { sx: { fontWeight: 'md' } } }}
              />
              <Typography level={Level.BODY_SM} sx={{ color: 'neutral.600', ml: 3.5 }}>
                {t('labelConvertToFamilySpecificHint')}
              </Typography>
              <Radio value={DetachedAttributes.DELETE} label={t('labelDeleteTheseAttributes')} />
              <Typography level={Level.BODY_SM} sx={{ color: 'neutral.600', ml: 3.5 }}>
                {t('labelDeleteTheseAttributesHint')}
              </Typography>
            </RadioGroup>
          </Stack>
        }
        confirmBtnLabel={t('buttonLabelSaveChanges')}
        onConfirm={() => {
          applyProductType(detached);
          setHoldOn(null);
          setIsDetailsOpen(false);
        }}
        onCancel={() => setHoldOn(null)}
      />

      <Confirmation
        isOpen={isLeaveConfirmOpen}
        color={Color.WARNING}
        title={t('titleUnsavedChanges')}
        message={
          <Stack sx={{ gap: 1 }}>
            <Typography level={Level.BODY_MD}>{t('messageUnsavedChangesLost')}</Typography>
            {unsavedChangeLines.length > 0 && (
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
                {unsavedChangeLines.map((line) => (
                  <Typography key={line} component="li" level={Level.BODY_MD}>
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        }
        confirmBtnLabel={t('buttonLabelLeavePage')}
        confirmBtnColor={Color.WARNING}
        cancelBtnLabel={t('buttonLabelStayOnPage')}
        onConfirm={leave}
        onCancel={stay}
      />
    </Stack>
  );
};

interface IProps {
  familyId: number;
}

// Loads the family and its parts, then edits them.
export const PartFinderFamilyEditor = ({ familyId }: IProps) => {
  const { t } = useTranslation();

  const {
    data: family,
    isLoading: isFamilyLoading,
    isFetching: isFamilyFetching,
    isError: isFamilyError,
  } = useGetPartFinderFamily(familyId);
  const {
    data: partsData,
    isLoading: arePartsLoading,
    isFetching: arePartsFetching,
    isError: arePartsError,
  } = useGetPartFinderFamilyParts(familyId, Boolean(family));

  const loadedParts = useMemo(() => (partsData?.records ?? []).map(mapPartFromServer), [partsData]);
  const defaultValues = useMemo(
    () => (family ? mapPartFinderFamilyFormDefaults(family, loadedParts) : undefined),
    [family, loadedParts]
  );

  // The editor opens once the family and its parts are loaded: a form without its parts would save
  // the family without them.
  if (isFamilyError || (!isFamilyLoading && !family)) {
    return (
      <Alert variant={Variant.SOFT} color={Color.DANGER}>
        {t('errorPartFinderFamilyNotFound')}
      </Alert>
    );
  }

  if (arePartsError) {
    return (
      <Alert variant={Variant.SOFT} color={Color.DANGER}>
        {t('errorPartFinderFamilyPartsLoad')}
      </Alert>
    );
  }

  if (isFamilyLoading || arePartsLoading) {
    return <Loader />;
  }

  return (
    <FormProvider
      schema={upsertPartFinderFamilySchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <PartFinderFamilyEditorContent
        family={family}
        loadedParts={loadedParts}
        isRefreshing={isFamilyFetching || arePartsFetching}
      />
    </FormProvider>
  );
};
